import type { Edge } from 'reactflow';
import type { CustomNode } from '../types/nodes';
import type { ContextField, NodeContext } from '../types/workflow-context';
import { NODE_OUTPUT_DEFINITIONS } from '../types/workflow-context';

// Debug: Log available node output definitions

/**
 * Find all upstream nodes for a given target node by traversing backwards through the graph
 */
export function findUpstreamNodes(
	targetNodeId: string,
	nodes: CustomNode[],
	edges: Edge[]
): CustomNode[] {
	const upstreamNodes: CustomNode[] = [];
	const visited = new Set<string>();

	function traverse(nodeId: string) {
		if (visited.has(nodeId)) {
			return; // Avoid cycles (though DAG shouldn't have them)
		}
		visited.add(nodeId);

		// Find all edges that connect TO this node (incoming edges)
		const incomingEdges = edges.filter(edge => edge.target === nodeId);

		for (const edge of incomingEdges) {
			const sourceNode = nodes.find(node => node.id === edge.source);
			if (sourceNode) {
				upstreamNodes.push(sourceNode);
				// Recursively traverse upstream
				traverse(sourceNode.id);
			}
		}
	}

	traverse(targetNodeId);
	return upstreamNodes;
}

/**
 * Get all available context fields for a given node by collecting data from upstream nodes
 */
export function getAvailableFields(
	targetNodeId: string,
	nodes: CustomNode[],
	edges: Edge[]
): NodeContext[] {
	const upstreamNodes = findUpstreamNodes(targetNodeId, nodes, edges);
	const contexts: NodeContext[] = [];

	for (const node of upstreamNodes) {
		const outputDefinition = node.type ? NODE_OUTPUT_DEFINITIONS[node.type] : undefined;

		if (outputDefinition) {
			const fields = outputDefinition(node.data);

			if (fields.length > 0) {
				// For start nodes, always use the actual node ID for context storage
				// For other nodes, use storeAs if available, otherwise fall back to node.id
				const isStartNode = node.type.startsWith('start.');
				const effectiveNodeId = isStartNode
					? node.id
					: node.data.config?.storeAs?.trim() || node.id;

				contexts.push({
					nodeId: effectiveNodeId,
					nodeType: node.type!,
					nodeLabel: node.data.label || node.type || 'Unknown',
					fields,
				});
			}
		}
	}
	return contexts;
}

/**
 * Get all available fields flattened into a single array with node context
 */
export function getFlattenedFields(
	targetNodeId: string,
	nodes: CustomNode[],
	edges: Edge[]
): ContextField[] {
	const contexts = getAvailableFields(targetNodeId, nodes, edges);
	const fields: ContextField[] = [];

	for (const context of contexts) {
		for (const field of context.fields) {
			// For start nodes (start.webhook, start.form, start.schedule),
			// don't prepend the node ID since their data becomes the root context
			const isStartNode = context.nodeType.startsWith('start.');
			const fieldId = isStartNode ? field.id : `${context.nodeId}.${field.id}`;
			const fieldLabel = isStartNode ? field.label : `${context.nodeLabel} → ${field.label}`;
			const fieldDescription = isStartNode
				? field.description
				: `${field.description} (from ${context.nodeLabel})`;

			fields.push({
				...field,
				id: fieldId,
				label: fieldLabel,
				description: fieldDescription,
			});
		}
	}

	return fields;
}

/**
 * Parse a string and extract variable placeholders like {{variable_name}}
 */
export function extractVariables(text: string): string[] {
	const variableRegex = /\{\{([^}]+)\}\}/g;
	const variables: string[] = [];
	let match;

	while ((match = variableRegex.exec(text)) !== null) {
		variables.push(match[1]);
	}

	return variables;
}

/**
 * Check if a string contains variable placeholders
 */
export function hasVariables(text: string): boolean {
	return /\{\{[^}]+\}\}/.test(text);
}

/**
 * Replace variable placeholders with actual values
 * This would be used at runtime to resolve variables
 */
export function resolveVariables(text: string, context: Record<string, any>): string {
	return text.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
		const value = context[variableName];
		return value !== undefined ? String(value) : match;
	});
}

/**
 * Checks if a node is a trigger/start node
 */
export function isStartNode(node: CustomNode): boolean {
	return node.data.category === 'triggers' && node.data.type.startsWith('start.');
}

/**
 * Checks if a node is a legacy submission form node (not yet migrated)
 */
export function isLegacySubmissionFormNode(node: CustomNode): boolean {
	return node.data.type === 'submission-form' && node.data.category === 'integrations';
}

/**
 * Checks if there are any start nodes in the workflow
 */
export function hasStartNode(nodes: CustomNode[]): boolean {
	const result = nodes.some(node => isStartNode(node) || isLegacySubmissionFormNode(node));
	// Debug logging disabled to prevent console spam
	// console.log('🔍 hasStartNode check:', {
	//	nodes: nodes.map(n => ({ id: n.id, type: n.type, category: n.data.category })),
	//	hasStart: result,
	// });
	return result;
}

/**
 * Gets all start nodes in the workflow
 */
export function getStartNodes(nodes: CustomNode[]): CustomNode[] {
	return nodes.filter(node => isStartNode(node) || isLegacySubmissionFormNode(node));
}

/**
 * Checks if the workflow is empty (no nodes)
 */
export function isEmptyWorkflow(nodes: CustomNode[]): boolean {
	return nodes.length === 0;
}

/**
 * Checks if a node type is a trigger node type
 */
export function isTriggerNodeType(nodeType: string): boolean {
	return nodeType.startsWith('start.');
}

export interface ValidationResult {
	isValid: boolean;
	errors: string[];
	warnings: string[];
}

/**
 * Validates Editor Behavior Rules
 */
export function validateEditorRules(nodes: CustomNode[]): ValidationResult {
	const errors: string[] = [];
	const warnings: string[] = [];

	const startNodes = getStartNodes(nodes);

	// Rule: Must have exactly one start node if not empty
	if (nodes.length > 0) {
		if (startNodes.length === 0) {
			errors.push('Workflow must start with a trigger node');
		} else if (startNodes.length > 1) {
			errors.push('Workflow can only have one trigger node');
		}
	}

	return {
		isValid: errors.length === 0,
		errors,
		warnings,
	};
}

/**
 * Convert complex condition configuration to simple expression string for backend
 * @param config - Complex condition configuration from frontend
 * @returns Simple expression string for backend ConditionProcessor
 */
export function convertConditionConfigToExpression(config: any): string {
	if (!config || !config.conditions || !Array.isArray(config.conditions)) {
		return '';
	}

	// If there's only one condition and it's advanced, use its expression directly
	if (config.conditions.length === 1 && config.conditions[0].type === 'advanced') {
		return config.conditions[0].expression || '';
	}

	// Convert simple conditions to expressions
	const conditionExpressions = config.conditions
		.map((condition: any) => {
			if (condition.type === 'advanced') {
				return condition.expression || '';
			}

			if (condition.type === 'simple') {
				const { field, operator, value } = condition;
				if (!field || !operator) return '';

				// Handle different operators
				switch (operator) {
					case '=':
						return `{{${field}}} == ${formatValue(value)}`;
					case '!=':
						return `{{${field}}} != ${formatValue(value)}`;
					case '>':
						return `Number({{${field}}}) > Number(${formatValue(value)})`;
					case '<':
						return `Number({{${field}}}) < Number(${formatValue(value)})`;
					case '>=':
						return `Number({{${field}}}) >= Number(${formatValue(value)})`;
					case '<=':
						return `Number({{${field}}}) <= Number(${formatValue(value)})`;
					case 'contains':
						return `String({{${field}}}).includes(${formatValue(value)})`;
					case 'startsWith':
						return `String({{${field}}}).startsWith(${formatValue(value)})`;
					case 'endsWith':
						return `String({{${field}}}).endsWith(${formatValue(value)})`;
					case 'isEmpty':
						return `!{{${field}}} || {{${field}}} === '' || {{${field}}} === null || {{${field}}} === undefined || (Array.isArray({{${field}}}) && {{${field}}}.length === 0) || (typeof {{${field}}} === 'object' && Object.keys({{${field}}}).length === 0)`;
					case 'isNotEmpty':
						return `{{${field}}} && {{${field}}} !== '' && {{${field}}} !== null && {{${field}}} !== undefined && (!Array.isArray({{${field}}}) || {{${field}}}.length > 0) && (typeof {{${field}}} !== 'object' || Object.keys({{${field}}}).length > 0)`;
					case 'isTrue':
						return `{{${field}}} === true`;
					case 'isFalse':
						return `{{${field}}} === false`;
					case 'hasProperty':
						return `{{${field}}} && typeof {{${field}}} === 'object' && ${formatValue(
							value
						)} in {{${field}}}`;
					case 'length=':
						return `Array.isArray({{${field}}}) && {{${field}}}.length === ${
							Number(value) || 0
						}`;
					case 'length>':
						return `Array.isArray({{${field}}}) && {{${field}}}.length > ${
							Number(value) || 0
						}`;
					case 'length<':
						return `Array.isArray({{${field}}}) && {{${field}}}.length < ${
							Number(value) || 0
						}`;
					default:
						return '';
				}
			}

			return '';
		})
		.filter(expr => expr !== '');

	if (conditionExpressions.length === 0) {
		return '';
	}

	// Combine expressions based on match logic
	const operator = config.match === 'any' ? ' || ' : ' && ';
	return conditionExpressions.join(operator);
}

/**
 * Convert simple expression string back to complex condition configuration for frontend
 * @param expression - Simple expression string from backend
 * @returns Complex condition configuration for frontend
 */
export function convertExpressionToConditionConfig(expression: string): any {
	if (!expression || typeof expression !== 'string') {
		return {
			match: 'all',
			conditions: [
				{
					type: 'simple',
					field: '',
					operator: '=',
					value: '',
				},
			],
		};
	}

	// If the expression looks like it was converted from a complex config, try to parse it
	// This is a simplified parser - for complex expressions, we'll default to advanced mode
	const trimmedExpression = expression.trim();

	// Check if it's a simple comparison that we can parse back
	const simpleComparisonRegex = /^{{([^}]+)}}\s*([=!<>]=?|>|<|>=|<=)\s*(.+)$/;
	const match = trimmedExpression.match(simpleComparisonRegex);

	if (match) {
		const [, field, operator, value] = match;
		let parsedValue = value.trim();

		// Remove quotes if present
		if (
			(parsedValue.startsWith('"') && parsedValue.endsWith('"')) ||
			(parsedValue.startsWith("'") && parsedValue.endsWith("'"))
		) {
			parsedValue = parsedValue.slice(1, -1);
		}

		// Convert operator back to frontend format
		let frontendOperator = operator;
		if (operator === '==') frontendOperator = '=';
		if (operator === '!=') frontendOperator = '!=';

		return {
			match: 'all',
			conditions: [
				{
					type: 'simple',
					field: field.trim(),
					operator: frontendOperator,
					value: parsedValue,
				},
			],
		};
	}

	// For complex expressions, use advanced mode
	return {
		match: 'all',
		conditions: [
			{
				type: 'advanced',
				expression: trimmedExpression,
			},
		],
	};
}

/**
 * Format value for JavaScript expression
 * @param value - Value to format
 * @returns Formatted value string
 */
function formatValue(value: any): string {
	if (value === null || value === undefined) {
		return 'null';
	}

	if (typeof value === 'boolean') {
		return value.toString();
	}

	if (typeof value === 'number') {
		return value.toString();
	}

	if (typeof value === 'string') {
		// Check if it's a boolean string
		if (value.toLowerCase() === 'true') return 'true';
		if (value.toLowerCase() === 'false') return 'false';

		// Check if it's a number string
		if (!isNaN(Number(value)) && value.trim() !== '') {
			return value;
		}

		// It's a regular string, quote it
		return `"${value.replace(/"/g, '\\"')}"`;
	}

	// For objects/arrays, stringify them
	return JSON.stringify(value);
}
